import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, saveToken, getToken, formatApiErrorDetail } from "@/lib/api";

export default function AdminLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (getToken()) {
      api
        .get("/auth/me")
        .then(() => navigate("/admin", { replace: true }))
        .catch(() => {});
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      saveToken(data.access_token);
      toast.success(`Welcome back, ${data.user.name || data.user.email}`);
      navigate("/admin", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="page-admin-login"
      className="min-h-screen bg-navy text-paper flex items-center justify-center px-6 py-12 relative overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/12306417/pexels-photo-12306417.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(5,28,44,0.85) 0%, rgba(5,28,44,0.95) 100%)",
        }}
        aria-hidden="true"
      />

      <div className="relative w-full max-w-md">
        <a
          href="/"
          data-testid="link-back-home"
          className="eyebrow text-gold hover:text-paper transition-colors inline-block mb-8"
        >
          ← Back to site
        </a>
        <p className="eyebrow text-gold mb-4">Editor sign-in</p>
        <h1 className="font-serif text-4xl md:text-5xl leading-tight mb-4">
          Welcome back, Nimisha.
        </h1>
        <p className="text-paper/60 mb-10 text-sm">
          Sign in to publish and edit insights.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="form-login">
          <div>
            <label className="eyebrow text-paper/60 block mb-2">Email</label>
            <input
              data-testid="input-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border border-paper/30 focus:border-gold outline-none px-4 py-3 text-paper placeholder:text-paper/30 transition-colors"
              placeholder="you@domain.com"
              autoComplete="username"
            />
          </div>
          <div>
            <label className="eyebrow text-paper/60 block mb-2">Password</label>
            <input
              data-testid="input-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border border-paper/30 focus:border-gold outline-none px-4 py-3 text-paper placeholder:text-paper/30 transition-colors"
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </div>

          {error && (
            <p
              data-testid="login-error"
              className="text-sm text-red-300 border border-red-400/40 bg-red-900/20 px-3 py-2"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="btn-submit-login"
            className="w-full bg-gold hover:bg-gold-light text-navy px-6 py-3.5 font-medium disabled:opacity-50 transition-colors"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
